<template>
  <div class="tab-shell">
    <div class="tab-head">
      <h2 class="tab-title">Fornecedores</h2>
      <button v-if="!showForm" class="btn-primary" type="button" @click="openForm()">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Novo Fornecedor
      </button>
    </div>

    <!-- Form -->
    <div v-if="showForm" class="form-card">
      <h3 class="form-title">{{ editId ? 'Editar Fornecedor' : 'Novo Fornecedor' }}</h3>
      <div class="form-grid">
        <label class="field">
          <span class="field-label">CNPJ</span>
          <input class="field-input" v-model="form.cnpj" placeholder="00.000.000/0000-00" maxlength="18" @input="maskCnpj" />
        </label>
        <label class="field">
          <span class="field-label">Razão Social *</span>
          <input class="field-input" v-model="form.razaoSocial" placeholder="Nome da empresa" />
        </label>
        <label class="field">
          <span class="field-label">Nome Fantasia</span>
          <input class="field-input" v-model="form.nomeFantasia" placeholder="Como é conhecido" />
        </label>
        <label class="field">
          <span class="field-label">Telefone</span>
          <input class="field-input" v-model="form.telefone" placeholder="(11) 99999-9999" />
        </label>
        <label class="field">
          <span class="field-label">E-mail</span>
          <input class="field-input" type="email" v-model="form.email" placeholder="contato@empresa.com" />
        </label>
        <label class="field">
          <span class="field-label">Condição de Pagamento</span>
          <input class="field-input" v-model="form.condicaoPagamento" placeholder="Ex: 30 dias, boleto" />
        </label>
        <label class="field field--full">
          <span class="field-label">Categorias (vírgula para separar)</span>
          <input class="field-input" v-model="categoriasText" placeholder="materiais, ferramentas, serviços" />
        </label>
        <label class="field field--full">
          <span class="field-label">Observações</span>
          <textarea class="field-input field-textarea" v-model="form.notes" rows="2" placeholder="Notas internas"></textarea>
        </label>
      </div>
      <p v-if="formError" class="form-error">{{ formError }}</p>
      <div class="form-actions">
        <button class="btn-save" type="button" :disabled="saving" @click="save">{{ saving ? 'Salvando...' : 'Salvar' }}</button>
        <button class="btn-cancel" type="button" @click="closeForm">Cancelar</button>
      </div>
    </div>

    <!-- List -->
    <div v-if="loading" class="empty-hint">Carregando...</div>
    <div v-else-if="suppliers.length === 0 && !showForm" class="empty-hint">
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
      Nenhum fornecedor cadastrado.
    </div>
    <div v-else class="cards-grid">
      <article v-for="s in suppliers" :key="s.id" class="supplier-card">
        <div class="card-body">
          <div class="card-title">{{ s.nomeFantasia || s.razaoSocial }}</div>
          <div v-if="s.nomeFantasia" class="card-meta">{{ s.razaoSocial }}</div>
          <div v-if="s.cnpj" class="card-meta">CNPJ: {{ formatCnpj(s.cnpj) }}</div>
          <div v-if="s.telefone" class="card-meta">{{ s.telefone }}</div>
          <div v-if="s.email" class="card-meta">{{ s.email }}</div>
          <div v-if="s.condicaoPagamento" class="card-meta">Pagamento: {{ s.condicaoPagamento }}</div>
          <div v-if="parsedCategorias(s).length" class="chip-row">
            <span v-for="cat in parsedCategorias(s)" :key="cat" class="cat-chip">{{ cat }}</span>
          </div>
        </div>
        <div class="card-footer">
          <button class="card-btn" type="button" @click="openForm(s)">Editar</button>
          <button class="card-btn card-btn--warn" type="button" @click="remove(s.id)">Excluir</button>
        </div>
      </article>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { apiFetch } from '@/store/api'
import { uiStore } from '@/store/ui'

interface Supplier {
  id: string; cnpj: string; razaoSocial: string; nomeFantasia: string
  telefone: string; email: string; categorias: string; condicaoPagamento: string; notes: string
}

const emptyForm = () => ({ cnpj: '', razaoSocial: '', nomeFantasia: '', telefone: '', email: '', condicaoPagamento: '', notes: '' })

export default defineComponent({
  name: 'OfficeFornecedoresTab',
  setup() {
    const suppliers = ref<Supplier[]>([])
    const loading = ref(false)
    const showForm = ref(false)
    const editId = ref('')
    const form = ref(emptyForm())
    const categoriasText = ref('')
    const formError = ref('')
    const saving = ref(false)

    const load = async () => {
      loading.value = true
      try { suppliers.value = await apiFetch<Supplier[]>('/office/suppliers') } finally { loading.value = false }
    }

    const formatCnpj = (v: string) => {
      const d = v.replace(/\D/g, '').padEnd(14, '0')
      return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12,14)}`
    }

    const maskCnpj = () => {
      const d = form.value.cnpj.replace(/\D/g, '').slice(0, 14)
      let r = d
      if (d.length > 12) r = `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`
      else if (d.length > 8) r = `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`
      else if (d.length > 5) r = `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`
      else if (d.length > 2) r = `${d.slice(0,2)}.${d.slice(2)}`
      form.value.cnpj = r
    }

    const parsedCategorias = (s: Supplier) => {
      try { return JSON.parse(s.categorias || '[]') } catch { return [] }
    }

    const openForm = (s?: Supplier) => {
      if (s) {
        editId.value = s.id
        form.value = { cnpj: formatCnpj(s.cnpj), razaoSocial: s.razaoSocial, nomeFantasia: s.nomeFantasia, telefone: s.telefone, email: s.email, condicaoPagamento: s.condicaoPagamento, notes: s.notes }
        categoriasText.value = parsedCategorias(s).join(', ')
      } else {
        editId.value = ''
        form.value = emptyForm()
        categoriasText.value = ''
      }
      formError.value = ''; showForm.value = true
    }

    const closeForm = () => { showForm.value = false; editId.value = ''; formError.value = '' }

    const save = async () => {
      if (!form.value.razaoSocial.trim()) { formError.value = 'Razão social é obrigatória.'; return }
      saving.value = true; formError.value = ''
      try {
        const categorias = categoriasText.value.split(',').map(c => c.trim()).filter(Boolean)
        const body = { ...form.value, categorias }
        if (editId.value) await apiFetch(`/office/suppliers/${editId.value}`, { method: 'PUT', body: JSON.stringify(body) })
        else await apiFetch('/office/suppliers', { method: 'POST', body: JSON.stringify(body) })
        uiStore.toast(editId.value ? 'Fornecedor atualizado.' : 'Fornecedor criado.', 'success')
        closeForm(); await load()
      } catch (e: unknown) { formError.value = e instanceof Error ? e.message : 'Erro ao salvar.' } finally { saving.value = false }
    }

    const remove = async (id: string) => {
      const ok = await uiStore.confirm('Excluir este fornecedor?')
      if (!ok) return
      try {
        await apiFetch(`/office/suppliers/${id}`, { method: 'DELETE' })
        uiStore.toast('Fornecedor excluído.', 'success')
        await load()
      } catch (e: unknown) { uiStore.toast(e instanceof Error ? e.message : 'Erro ao excluir.', 'error') }
    }

    onMounted(load)
    return { suppliers, loading, showForm, editId, form, categoriasText, formError, saving, formatCnpj, maskCnpj, parsedCategorias, openForm, closeForm, save, remove }
  }
})
</script>

<style scoped lang="scss">
.tab-shell { width: 100%; }
.tab-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.tab-title { margin: 0; font-size: 18px; font-weight: 800; color: var(--ink, #2c2b27); }
.btn-primary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff);
  font-size: 13px; font-weight: 600;
  &:hover { opacity: .88; }
}
.form-card { background: var(--card, #fff); border: 1px solid rgba(31,30,26,.1); border-radius: 14px; padding: 20px; margin-bottom: 24px; }
.form-title { margin: 0 0 16px; font-size: 15px; font-weight: 700; color: var(--ink, #2c2b27); }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.field { display: flex; flex-direction: column; gap: 4px; }
.field--full { grid-column: 1 / -1; }
.field-label { font-size: 12px; font-weight: 600; color: var(--ink-muted, #888); }
.field-input {
  padding: 8px 11px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.18);
  font-size: 13px; outline: none; background: #fff;
  &:focus { border-color: var(--system-navbar, #0f4c81); }
}
.field-textarea { resize: vertical; }
.form-error { font-size: 12px; color: #dc2626; margin: 8px 0 0; }
.form-actions { display: flex; gap: 8px; margin-top: 16px; }
.btn-save {
  padding: 8px 18px; border-radius: 7px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: #fff; font-size: 13px; font-weight: 600;
  &:disabled { opacity: .6; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: .88; }
}
.btn-cancel {
  padding: 8px 14px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.18);
  background: #fff; cursor: pointer; font-size: 13px; font-weight: 500;
  &:hover { background: rgba(31,30,26,.04); }
}
.empty-hint {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 60px 20px; color: var(--ink-muted, #aaa); text-align: center; font-size: 14px;
  svg { opacity: .35; }
}
.cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 14px; }
.supplier-card {
  border: 1px solid rgba(31,30,26,.1); border-radius: 14px; background: var(--card, #fff);
  display: flex; flex-direction: column;
}
.card-body { padding: 14px 16px 10px; flex: 1; display: flex; flex-direction: column; gap: 3px; }
.card-title { font-size: 14px; font-weight: 700; color: var(--ink, #2c2b27); }
.card-meta { font-size: 12px; color: var(--ink-muted, #888); }
.chip-row { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
.cat-chip { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 20px; background: #e0f2fe; color: #0369a1; }
.card-footer { display: flex; gap: 2px; padding: 8px 10px; border-top: 1px solid rgba(31,30,26,.07); }
.card-btn {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 9px; border-radius: 6px; border: none; cursor: pointer;
  background: transparent; color: var(--ink-muted, #666); font-size: 11px; font-weight: 500;
  transition: background .12s;
  &:hover { background: rgba(31,30,26,.06); color: var(--ink, #2c2b27); }
  &--warn:hover { background: #fff7ed; color: #c2410c; }
}
@media (max-width: 600px) {
  .form-grid { grid-template-columns: 1fr; }
  .cards-grid { grid-template-columns: 1fr; }
}
</style>
